import { useIsAdminUser } from "@mirage/service-auth/useDropboxAccount";
import { useDashUserAttributes } from "@mirage/shared/eligibility/hooks/useDashUserAttributes";
import Sentry from "@mirage/shared/sentry";
import i18n from "@mirage/translations";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCanProvisionFreeDashTeam } from "../hooks/useCanProvisionFreeDashTeam";
import { ActionButtonType } from "../self-serve-onboarding/SelfServeOnboarding";
import { SelfServeOnboardingStep } from "../self-serve-onboarding/types";
import { useFssTeamInfo } from "./useFssTeamInfo";
import { useSelfServeFssSurveyResponses } from "./useSelfServeFssSurveyResponses";
import { useSelfServeOnboardingState } from "./useSelfServeOnboardingState";

import useAvailableExtensions from "@mirage/settings/utils/useAvailableExtensions";
import type { OnboardingStepItem } from "../self-serve-onboarding/SelfServeOnboarding";

import { ConnectorsDescription } from "../flows/ConnectorsDescription";

export const removeStepFromFlow = (
  steps: OnboardingStepItem[],
  step: SelfServeOnboardingStep,
) => {
  const index = steps.findIndex((s) => s.step === step);

  if (index > -1) {
    steps.splice(index, 1);
  }
};

const EXTENSION_UPSELL_STEP = {
  step: SelfServeOnboardingStep.DOWNLOAD_EXTENSION_UPSELL,
  title: i18n.t("team_onboarding_extension_upsell_title"),
  description: i18n.t("team_onboarding_extension_upsell_description"),
  primaryActions: [ActionButtonType.Later],
  skipValidation: true,
};

const DESKTOP_UPSELL_STEP = {
  step: SelfServeOnboardingStep.DOWNLOAD_UPSELL,
  title: i18n.t("self_serve_download_upsell_title"),
  description: i18n.t("self_serve_download_upsell_description"),
  primaryActions: [ActionButtonType.Later, ActionButtonType.Download],
  skipValidation: true,
};

const ADMIN_ONBOARDING_STEPS: OnboardingStepItem[] = [
  {
    step: SelfServeOnboardingStep.COMPANY_INFO,
    title: i18n.t("self_serve_company_info_title"),
    description: i18n.t("self_serve_company_info_description"),
    primaryActions: [ActionButtonType.Continue],
  },
  {
    step: SelfServeOnboardingStep.PROFILING,
    title: i18n.t("self_serve_profiling_title"),
    description: i18n.t("self_serve_profiling_description"),
    primaryActions: [ActionButtonType.Continue],
  },
  {
    step: SelfServeOnboardingStep.JTBD,
    title: i18n.t("self_serve_jtbd_title"),
    description: i18n.t("self_serve_jtbd_description"),
    primaryActions: [ActionButtonType.Continue],
  },
  {
    step: SelfServeOnboardingStep.CONNECTORS,
    title: i18n.t("self_serve_connectors_title"),
    description: <ConnectorsDescription />,
    primaryActions: [ActionButtonType.Continue],
    skipValidation: true,
  },
  {
    step: SelfServeOnboardingStep.TEAM_INVITE,
    title: i18n.t("self_serve_team_invite_title"),
    primaryActions: [ActionButtonType.Skip, ActionButtonType.Continue],
  },
  // Creating a separate step for large team onboarding and avoid showing it in the main flow
  {
    step: SelfServeOnboardingStep.LARGE_TEAM,
    title: "", // This step is hidden, so it does not need a title or description
    description: "",
    primaryActions: [],
    skipValidation: true,
    hidden: true, // This step is hidden because it is outside of the main flow of onboarding
  },
  {
    step: SelfServeOnboardingStep.ERROR,
    title: "", // This step is hidden, so it does not need a title or description
    description: "",
    primaryActions: [],
    skipValidation: true,
    hidden: true, // This step is hidden because it is outside of the main flow of onboarding
  },
];

const TEAM_MEMBER_ONBOARDING_STEPS: OnboardingStepItem[] = [
  {
    step: SelfServeOnboardingStep.PROFILING,
    title: i18n.t("self_serve_profiling_title"),
    description: i18n.t("self_serve_profiling_description"),
    primaryActions: [ActionButtonType.Continue],
  },
  {
    step: SelfServeOnboardingStep.CONNECTORS,
    title: i18n.t("self_serve_connectors_title"),
    description: <ConnectorsDescription />,
    primaryActions: [ActionButtonType.Continue],
  },
  {
    step: SelfServeOnboardingStep.TEAM_INVITE,
    title: i18n.t("self_serve_team_invite_title"),
    primaryActions: [ActionButtonType.Skip, ActionButtonType.Continue],
  },
];

// Combines FSS team info and Dash team info to provide a unified view of the team information
const useGetDashTeamInfo = () => {
  const [loading, setLoading] = useState(true);
  const hasHydrated = useRef(false);

  const { userAttributes, refetch: refetchDashUserAttributes } =
    useDashUserAttributes();

  const isAdmin = useIsAdminUser();
  const { canProvisionDashFreemiumTeam } = useCanProvisionFreeDashTeam();

  const {
    companyName: dashTeamName,
    companyLogo: dashTeamLogo,
    setCompanyName: setDashTeamName,
    setCompanyLogo: setDashTeamLogo,
    loading: loadingDashOnboardingState,
  } = useSelfServeOnboardingState();

  const {
    teamName: fssTeamName,
    teamLogo: fssTeamLogo,
    loading: loadingFssTeamInfo,
  } = useFssTeamInfo(isAdmin);

  const refreshSteps = useCallback(async () => {
    await refetchDashUserAttributes();
  }, [refetchDashUserAttributes]);

  useEffect(() => {
    const hydrateTeamInfo = async () => {
      if (loadingDashOnboardingState || loadingFssTeamInfo) {
        return;
      }

      if ((!isAdmin && !canProvisionDashFreemiumTeam) || hasHydrated.current) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        hasHydrated.current = true;

        if (fssTeamName && !dashTeamName) {
          setDashTeamName(fssTeamName);
        }

        if (fssTeamLogo && !dashTeamLogo) {
          const dataUrl = `data:image/png;base64,${fssTeamLogo}`;
          setDashTeamLogo(dataUrl);
        }

        // Refresh steps only once and only if teamName is available
        if (fssTeamName) {
          refreshSteps();
        }
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setLoading(false);
      }
    };

    hydrateTeamInfo();
  }, [
    isAdmin,
    loadingDashOnboardingState,
    dashTeamName,
    dashTeamLogo,
    setDashTeamName,
    setDashTeamLogo,
    loadingFssTeamInfo,
    fssTeamName,
    fssTeamLogo,
    canProvisionDashFreemiumTeam,
    refreshSteps,
  ]);

  return {
    canSkipCompanyInfoStep: !!fssTeamName,
    canSeeDfbInviteMemberForm: !!userAttributes?.canSeeDfbInviteMemberForm,
    loading: loading || loadingFssTeamInfo || loadingDashOnboardingState,
  };
};

export const useSelfServeOnboardingSteps = (): {
  loading: boolean;
  onboardingSteps: OnboardingStepItem[];
} => {
  const { canSkipCompanyInfoStep, canSeeDfbInviteMemberForm, loading } =
    useGetDashTeamInfo();
  const isAdmin = useIsAdminUser();
  const { canProvisionDashFreemiumTeam } = useCanProvisionFreeDashTeam();
  const { extensionAvailableForBrowser } = useAvailableExtensions();
  const { surveyResponses } = useFssTeamInfo(isAdmin);
  const { answeredProfilingQuestions: canSkipProfilingStep } =
    useSelfServeFssSurveyResponses(
      isAdmin,
      canProvisionDashFreemiumTeam,
      surveyResponses,
    );

  const onboardingSteps = useMemo(() => {
    const showAdminSteps = isAdmin || canProvisionDashFreemiumTeam;

    const baseSteps = showAdminSteps
      ? ADMIN_ONBOARDING_STEPS
      : TEAM_MEMBER_ONBOARDING_STEPS;

    const upsellStep = extensionAvailableForBrowser
      ? EXTENSION_UPSELL_STEP
      : DESKTOP_UPSELL_STEP;

    const steps = [...baseSteps, upsellStep];

    if (!canSeeDfbInviteMemberForm) {
      removeStepFromFlow(steps, SelfServeOnboardingStep.TEAM_INVITE);
    }

    if (canSkipCompanyInfoStep) {
      removeStepFromFlow(steps, SelfServeOnboardingStep.COMPANY_INFO);
    }

    if (canSkipProfilingStep) {
      removeStepFromFlow(steps, SelfServeOnboardingStep.PROFILING);
    }

    return steps;
  }, [
    isAdmin,
    canProvisionDashFreemiumTeam,
    canSeeDfbInviteMemberForm,
    extensionAvailableForBrowser,
    canSkipCompanyInfoStep,
    canSkipProfilingStep,
  ]);

  return {
    loading,
    onboardingSteps,
  };
};
